// START IMPORTS
import { CustomIcons } from '@ltvco/refresh-lib/theme';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { Phone, Radar, Person2, Work, TaskAlt } from '@mui/icons-material';

import type { Owner } from '@ltvco/refresh-lib/v1';
// END IMPORTS

// START INTERFACES_AND_TYPES
type DataCounts = {
  potentialOwnersLength: number;
  searchVolumeLength: number;
  commentsLength: number;
  neighborSpoofingLength: number;
  spamReportsCount: number;
};
// END INTERFACES_AND_TYPES

// START HELPER_FUNCTIONS
export const getPhoneNavLinkData = (owner: Owner, dataCounts: DataCounts) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;
  const isPhonePartialAddress = useFeatureIsOn('OAR_1707');
  return [
    {
      href: '#phone-overview',
      sectionId: '#phone-overview',
      text: 'Overview',
      reportItemsCount: null,
      hideReportItemsCount: true,
      notificationSection: 'identity',
    },
    {
      href: '#phone-fraudscan-section',
      sectionId: '#phone-fraudscan-section',
      text: 'FraudScan Summary',
      reportItemsCount: null,
      hideReportItemsCount: true,
    },
    {
      href: '#potential-owners-section',
      sectionId: '#potential-owners-section',
      text: 'Potential Owners',
      reportItemsCount: dataCounts.potentialOwnersLength,
      notificationSection: 'owners',
    },
    {
      href: '#possible-photos-section',
      sectionId: '#possible-photos-section',
      text: 'Possible Photos',
      reportItemsCount: owner.images?.length || null,
      notificationSection: 'photos',
    },
    {
      href: '#phone-numbers-section',
      sectionId: '#phone-numbers-section',
      text: 'Other Phone Numbers',
      reportItemsCount: Math.max(owner.phones.length - 1, 0),
      notificationSection: 'phones',
    },
    {
      href: '#email-section',
      sectionId: '#email-section',
      text: 'Email Addresses',
      reportItemsCount: owner.emails.length,
      notificationSection: 'emails',
    },
    {
      href: '#address-history-section',
      sectionId: '#address-history-section',
      text: isPhonePartialAddress ? 'Possible Locations' : 'Address History',
      reportItemsCount: owner.addresses.length,
      notificationSection: 'addresses',
    },
    {
      href: '#jobs-section',
      sectionId: '#jobs-section',
      text: 'Work History',
      reportItemsCount: owner.jobs.length,
      notificationSection: 'jobs',
    },
    {
      href: '#education-section',
      sectionId: '#education-section',
      text: 'Educational Background',
      reportItemsCount: owner.educations.length,
      notificationSection: 'educations',
    },
    {
      href: '#social-media-section',
      sectionId: '#social-media-section',
      text: 'Social Media',
      reportItemsCount: owner.profiles.length + owner.usernames.length,
      notificationSection: 'socials',
    },
    {
      href: '#neighbor-spoofing-section',
      sectionId: '#neighbor-spoofing-section',
      text: 'Neighbor Spoofing',
      reportItemsCount: dataCounts.neighborSpoofingLength,
      notificationSection: 'neighbors',
    },
    {
      href: '#search-volume-section',
      sectionId: '#search-volume-section',
      text: 'Search Volume',
      reportItemsCount: dataCounts.searchVolumeLength,
    },
    {
      href: '#nuisance-caller-section',
      sectionId: '#nuisance-caller-section',
      text: 'Nuisance Caller',
      reportItemsCount: dataCounts.spamReportsCount,
    },
    {
      href: '#comments-section',
      sectionId: '#comments-section',
      text: 'Comments',
      reportItemsCount: dataCounts.commentsLength,
    },
    {
      href: '#possible-owned-assets-section',
      sectionId: '#possible-owned-assets-section',
      text: 'Assets',
      reportItemsCount: assetsCount,
      notificationSection: 'real_estate',
    },
  ];
};

export const getPhoneNavLinkDataV2 = (owner: Owner, dataCounts: DataCounts) => {
  const assetsCount =
    owner.ownedAssets['real_estate'].length +
    owner.ownedAssets['vehicles'].aircraft.length +
    owner.ownedAssets['vehicles'].watercraft.length +
    owner.ownedAssets['vehicles'].automobiles.length;
  const isPhonePartialAddress = useFeatureIsOn('OAR_1707');
  return [
    {
      href: '#phone-overview',
      labelText: 'Overview',
      icons: [<Phone sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="large" />],
    },
    {
      labelText: 'FraudScan',
      icons: [<Radar sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="small" />],
      childItems: [
        {
          href: '#phone-fraudscan-section',
          labelText: 'Summary',
          hideReportItemsCount: true,
        },
        {
          href: '#neighbor-spoofing-section',
          labelText: 'Neighbor Spoofing',
          reportItemsCount: dataCounts.neighborSpoofingLength,
        },
        {
          href: '#search-volume-section',
          labelText: 'Search Volume',
          reportItemsCount: dataCounts.searchVolumeLength,
        },
        {
          href: '#nuisance-caller-section',
          labelText: 'Nuisance Caller',
          reportItemsCount: dataCounts.spamReportsCount,
        },
        {
          href: '#comments-section',
          labelText: 'Comments',
          reportItemsCount: dataCounts.commentsLength,
        },
      ],
    },
    {
      labelText: 'Potential Owners',
      icons: [
        <Person2 sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="small" />,
      ],
      childItems: [
        {
          href: '#potential-owners-section',
          labelText: 'Potential Owners',
          reportItemsCount: dataCounts.potentialOwnersLength,
        },
        {
          href: '#possible-photos-section',
          labelText: 'Possible Photos',
          reportItemsCount: owner.images?.length || null,
        },
      ],
    },
    {
      labelText: 'Contact Information',
      icons: [<Phone sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="small" />],
      childItems: [
        {
          href: '#phone-numbers-section',
          labelText: 'Phone Numbers',
          reportItemsCount: Math.max(owner.phones.length - 1, 0),
        },
        {
          href: '#email-section',
          labelText: 'Email Addresses',
          reportItemsCount: owner.emails.length,
        },
        {
          href: '#address-history-section',
          labelText: isPhonePartialAddress
            ? 'Possible Locations'
            : 'Address History',
          reportItemsCount: owner.addresses.length,
        },
        {
          href: '#social-media-section',
          sectionId: '#social-media-section',
          labelText: 'Social Media',
          reportItemsCount: owner.profiles.length + owner.usernames.length,
        },
      ],
    },
    {
      labelText: 'Jobs & Educations',
      icons: [<Work sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="small" />],
      childItems: [
        {
          href: '#jobs-section',
          sectionId: '#jobs-section',
          labelText: 'Jobs',
          reportItemsCount: owner.jobs.length,
        },
        {
          href: '#education-section',
          sectionId: '#education-section',
          labelText: 'Education',
          reportItemsCount: owner.educations.length,
        },
      ],
    },
    {
      labelText: 'Assets',
      icons: [<CustomIcons.OwnedAssetsIcon color="primary" fontSize="small" />],
      href: '#possible-owned-assets-section',
      reportItemsCount: assetsCount,
    },
    {
      labelText: 'Next Steps',
      icons: [
        <TaskAlt sx={{ color: 'rgba(0, 0, 0, 0.87)' }} fontSize="small" />,
      ],
      childItems: [
        {
          href: '#Notes-section',
          labelText: 'Notes',
          hideReportItemsCount: true,
        },
        {
          href: '#related-reports-section',
          labelText: 'Related Reports',
          hideReportItemsCount: true,
        },
        {
          href: '#report-rating',
          labelText: 'Report Rating',
          hideReportItemsCount: true,
        },
      ],
    },
  ];
};
// END HELPER_FUNCTIONS
